#!/usr/bin/env node

import { McpServer, type ToolCallback } from '@modelcontextprotocol/sdk/server/mcp.js'
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js'
import { z } from 'zod'
import {
  CheckPoliciesScopeSchema,
  DivergenceTypeSchema,
  HypotheticalActionSchema,
  type MCPToolName,
} from '@neat.is/types'
import { resolveBaseUrlWithSource } from './base-url.js'
import { createHttpClient } from './client.js'
import { checkEndpointIsNeat, describeForeignEndpoint } from './endpoint-check.js'
import { registerResources } from './resources.js'
import {
  ask,
  checkPolicies,
  expandNode,
  getBlastRadius,
  getDependencies,
  getDivergences,
  getGraphDiff,
  getIncidentCard,
  getIncidentHistory,
  getObservedDependencies,
  getRecentStaleEdges,
  getRootCause,
  neatApplyExtension,
  neatDescribeProjectInstrumentation,
  neatDryRunExtension,
  neatListUninstrumented,
  neatLookupInstrumentation,
  neatRollbackExtension,
  relate,
  semanticSearch,
} from './tools.js'
import {
  createConnectorDeps,
  neatConnect,
  neatConnectionStatus,
  neatDisconnect,
  neatListConnectable,
} from './connectors.js'

const resolved = resolveBaseUrlWithSource()
const baseUrl = resolved.url
// ADR-073 §3 + client-profiles.md §6 — the bearer comes from the same resolver
// that picked the URL, so a hosted profile's token rides with its endpoint while
// a local/loopback core still reads NEAT_AUTH_TOKEN. Empty/unset keeps the
// header off so a loopback dev core stays reachable.
const bearerToken = resolved.authToken
const client = createHttpClient(baseUrl, bearerToken)

// The hosted connector tools (ADR-228) talk to the CONTROL PLANE, not the daemon:
// NEAT_CP_URL + the durable neat_pat_ (NEAT_API_KEY). Env is the interim source; the
// login-written profile is the durable one, read through resolveCpTarget as a
// follow-on with env as the override (ADR-228 §5). Unset → the tools return a
// "not configured" message rather than erroring, so a local-only server stays clean.
const cpUrl = process.env.NEAT_CP_URL
const cpApiKey = process.env.NEAT_API_KEY
const connectorDeps =
  cpUrl && cpApiKey
    ? createConnectorDeps(createHttpClient(cpUrl, cpApiKey), process.env.NEAT_CP_PROJECT_ID)
    : null

// `NEAT_DEFAULT_PROJECT` is the implicit project for tool calls that don't
// pass a `project` arg. Unset means "use the core's `default` project" — we
// route those calls through the legacy unprefixed URL so an older core (one
// that predates #83) still gets the request it expects.
const defaultProject = process.env.NEAT_DEFAULT_PROJECT
const projectFor = (input: { project?: string }): string | undefined =>
  input.project ?? defaultProject

const projectField = z
  .string()
  .optional()
  .describe(
    'Project name when the core hosts more than one (set NEAT_PROJECTS=...). Omit to use the default project.',
  )

// Server-level orientation the MCP `initialize` handshake hands the connecting
// agent, so it knows what NEAT's data *is* before it reads a tool result. NEAT
// is one server among however many the agent has wired up — some of them
// (Supabase, Cloudflare, ...) may be the very platforms NEAT's connectors pull
// from. The line to draw: NEAT's tools answer from its own fused graph, not a
// live connection to those platforms, and every answer carries provenance so
// the agent can weigh it. The agent's other servers, and their overlap with
// NEAT's view, are the agent's own to reconcile — NEAT can't see its peers and
// doesn't try to; it just says plainly what its own data is.
const serverInstructions = [
  'NEAT serves a fused semantic graph of one software system — static code (EXTRACTED) and live runtime behavior (OBSERVED) in a single model — for the one project this daemon owns. Every tool answers from that graph.',
  'A result is a graph fact, not a live call to the underlying system. Each edge and result carries a provenance — OBSERVED (seen via OTel), INFERRED (stitched, ~0.6 confidence), EXTRACTED (from source/config), STALE (was observed, gone quiet) — plus a confidence. Trust a claim by its provenance.',
  'Some OBSERVED data is pulled by connectors from a provider that runs its own telemetry (Supabase, Railway, Firebase, Cloudflare). That is NEAT\'s own view of the provider, keyed on the provider node (an InfraNode carries `provider`; a service/file carries `platform`). If you also have that provider\'s own MCP server, NEAT is not it and does not replace it — NEAT tells you how the graph relates, the provider server acts on the live system.',
  'Reach for NEAT before grepping source for architecture-level questions: dependencies, runtime traffic, recent failures, blast radius, divergence between declared and observed. If a query comes back empty, confirm the daemon is up before falling back to reading files.',
].join('\n\n')

const server = new McpServer(
  {
    name: 'neat',
    version: '0.1.0',
  },
  { instructions: serverInstructions },
)

// Register every MCP tool through this wrapper, not server.tool directly.
// The tool name is constrained to MCP_TOOL_NAMES in @neat.is/types — add the
// name there first or this won't compile. The contracts audit also checks
// that registrations and the manifest match both ways, so the tool surface
// can't drift from the contract again.
const registerTool = <Args extends z.ZodRawShape>(
  name: MCPToolName,
  description: string,
  paramsSchema: Args,
  cb: ToolCallback<Args>,
): ReturnType<typeof server.tool> => server.tool(name, description, paramsSchema, cb)

registerTool(
  'ask',
  'Ask the graph a question in plain language — the front door to NEAT. Reach for this FIRST, before Read/Grep/Bash, for any question about this system\'s behaviour, dependencies, failures, root cause, or blast radius. You do NOT need to know which tool or the exact node id: `ask` resolves the entities in your question to graph nodes and routes it to the right traversal (root cause, dependencies, observed runtime calls, incidents, divergences, blast radius), returning one compact answer with every fact provenance-tagged (EXTRACTED/OBSERVED/INFERRED/STALE) and confidence-scored. Use the structured tools (get_root_cause, get_dependencies, …) when you already have a node id and want just that one traversal.',
  {
    question: z
      .string()
      .describe('A natural-language question, e.g. "why is checkout failing?" or "what breaks if I change the orders table?"'),
    project: projectField,
  },
  async (input) => ask(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_root_cause',
  'Trace a failing node up its dependency graph to find the underlying cause. Use this when something is breaking and you want to know which upstream component is the actual culprit.',
  {
    errorNode: z
      .string()
      .describe('Graph node id where the error surfaced, e.g. "database:payments-db"'),
    errorId: z
      .string()
      .optional()
      .describe('Specific error event id from incident history; if set, the result is coloured with that error message'),
    project: projectField,
  },
  async (input) => getRootCause(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_blast_radius',
  'List every node that depends on the given node — what would break if this node failed or was redeployed.',
  {
    nodeId: z.string().describe('Graph node id to compute blast radius from'),
    depth: z
      .number()
      .int()
      .nonnegative()
      .max(20)
      .optional()
      .describe('Max BFS depth (default 10)'),
    project: projectField,
  },
  async (input) => getBlastRadius(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_dependencies',
  'List the transitive outgoing dependencies of a node, BFS to depth N (default 3, max 10). Each result carries distance, edge type, and provenance — both static (EXTRACTED) and runtime (OBSERVED). Pass depth=1 for direct-only.',
  {
    nodeId: z.string().describe('Graph node id to inspect'),
    depth: z
      .number()
      .int()
      .min(1)
      .max(10)
      .optional()
      .describe('BFS depth (default 3, max 10). depth=1 returns direct dependencies only.'),
    project: projectField,
  },
  async (input) => getDependencies(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_observed_dependencies',
  'List only the runtime (OBSERVED via OTel) outgoing dependencies of a node. Use this to compare what code SAYS the service depends on vs what production actually does.',
  {
    nodeId: z.string().describe('Graph node id to inspect'),
    project: projectField,
  },
  async (input) => getObservedDependencies(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'expand',
  'Take one navigation step from a node and classify the neighbourhood (ADR-189). direction "up" walks to callers/dependents (who calls this), "down" walks to callees/dependencies (what this calls). Each neighbour comes back classified primary-failure / symptom-only / unrelated. Use this to navigate a failure one hop at a time instead of trusting a single verdict — a symptom-only node is a downstream victim, so walk "up" from it toward the real cause.',
  {
    nodeId: z.string().describe('Graph node id to step from'),
    direction: z
      .enum(['up', 'down'])
      .describe('up = callers/dependents (toward the cause), down = callees/dependencies'),
    project: projectField,
  },
  async (input) => expandNode(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'relate',
  'Confirm whether two nodes are connected, which way, and whether the connecting path carries the failure (ADR-189). Returns the direction (a→b or b→a), the path with per-hop provenance, and carriesSignal — whether errors/latency run end to end, which turns "a path exists" into "a is actually causing b". No path within the depth bound returns "no path within N hops", never a false "unrelated".',
  {
    a: z.string().describe('First node id (the hypothesised cause)'),
    b: z.string().describe('Second node id (the hypothesised symptom)'),
    maxDepth: z
      .number()
      .int()
      .min(1)
      .max(10)
      .optional()
      .describe('Max path length to search (default 5)'),
    project: projectField,
  },
  async (input) => relate(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_incident_history',
  'Return recent OTel error events recorded against a node, most recent first.',
  {
    nodeId: z.string().describe('Graph node id to query'),
    limit: z.number().int().positive().max(100).optional().describe('Max events to return (default 20)'),
    project: projectField,
  },
  async (input) => getIncidentHistory(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_incident_card',
  'One self-sufficient work order for an incident on a node (ADR-221): the incident fused with its root-cause chain, blast radius, governing policies, and node divergence — each claim provenance-stamped, so you can act without grepping. Give a node id (a service, file, or symbol); omit errorId for the node\'s most-recent incident, or pass errorId to pin one.',
  {
    nodeId: z.string().describe('Graph node id the incident is on (service/file/symbol)'),
    errorId: z
      .string()
      .optional()
      .describe('Pin a specific incident by its id; omit for the most recent'),
    project: projectField,
  },
  async (input) => getIncidentCard(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'semantic_search',
  'Search nodes by natural-language query. Uses embedding vectors when an embedder is available (Ollama nomic-embed-text → in-process MiniLM → substring fallback) — phrase the query the way you would describe what you want.',
  {
    query: z.string().describe('Free-text query, e.g. "service handling checkout payments"'),
    project: projectField,
  },
  async (input) => semanticSearch(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_graph_diff',
  'Diff a saved graph snapshot against the current live graph. Useful for change reviews and post-incidents — answers "what changed in the architecture between then and now." Returns added/removed/changed nodes and edges with both snapshot timestamps.',
  {
    againstSnapshot: z
      .string()
      .describe(
        'Path or http(s) URL of the snapshot to diff against (the "before" state). The current graph is the "after".',
      ),
    project: projectField,
  },
  async (input) => getGraphDiff(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_recent_stale_edges',
  'List the most recent OBSERVED → STALE edge transitions. Use this to spot integrations that have gone quiet — a CALLS edge that just went stale typically means an upstream stopped calling, not that the link is healthy.',
  {
    limit: z
      .number()
      .int()
      .positive()
      .max(200)
      .optional()
      .describe('Max events to return (default 50)'),
    edgeType: z
      .string()
      .optional()
      .describe('Filter by edge type — e.g. "CALLS" or "CONNECTS_TO"'),
    project: projectField,
  },
  async (input) => getRecentStaleEdges(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'get_divergences',
  "Returns places where what the code declares (EXTRACTED) doesn't match what production observed (OBSERVED). The single most NEAT-shaped query — the one that justifies the whole graph. Use when the user asks 'is anything weird?' or 'what does production do that the code doesn't?' or 'find me a bug' on an unfamiliar codebase. Returns divergences ranked by confidence × severity. Prefer this over `get_root_cause` when no specific node is failing.",
  {
    type: z
      .array(DivergenceTypeSchema)
      .optional()
      .describe(
        'Filter by divergence type. One or more of: missing-observed, missing-extracted, version-mismatch, host-mismatch, compat-violation, observed-symbol-mismatch, observed-failing. Omit for all.',
      ),
    minConfidence: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe('Drop divergences below this confidence threshold (0.0 - 1.0).'),
    node: z
      .string()
      .optional()
      .describe('Scope to divergences involving this node id (as source or target).'),
    project: projectField,
  },
  async (input) =>
    getDivergences(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'check_policies',
  'Inspect, dry-run, or get the soft guardrail for the project\'s policy.json. With applicableTo, returns the policies that apply where you are working — surfaced as context so you stay inside the lines (informs, never blocks). Without hypotheticalAction or applicableTo, returns currently-recorded violations. With hypotheticalAction, returns violations that would result if the action were applied. Architectural assertions in five shapes (structural / compatibility / provenance / ownership / blast-radius).',
  {
    scope: CheckPoliciesScopeSchema.optional().describe(
      'Narrow to a subset. Default "all".',
    ),
    hypotheticalAction: HypotheticalActionSchema.optional().describe(
      'Dry-run mode: simulate the action and return resulting violations. Omit for current state.',
    ),
    applicableTo: z
      .string()
      .optional()
      .describe(
        'Soft guardrail (ADR-108): pass the node id you are about to edit and check_policies returns the policies that govern it, as a context block — so you stay inside the lines. Advisory only; never blocks.',
      ),
    project: projectField,
  },
  async (input) =>
    checkPolicies(client, {
      ...input,
      project: projectFor(input),
    } as Parameters<typeof checkPolicies>[1]),
)

// ── /neat extend tools (ADR-081, ADR-086) ────────────────────────────────

registerTool(
  'neat_list_uninstrumented',
  'List libraries in the project that need instrumentation beyond the auto-instrumentations bundle. Returns first-party, third-party, and gap libraries that require an explicit instrumentation package.',
  { project: projectField },
  async (input) => neatListUninstrumented(client, { project: projectFor(input) }),
)

registerTool(
  'neat_lookup_instrumentation',
  'Look up the registry entry for a specific library. Returns the canonical instrumentation package, version, and registration snippet if one exists.',
  {
    library: z.string().describe('npm package name, e.g. "@prisma/client"'),
    installedVersion: z.string().optional().describe('Installed version for range matching'),
    project: projectField,
  },
  async (input) => neatLookupInstrumentation(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'neat_describe_project_instrumentation',
  'Describe the current state of OTel instrumentation in the project: which hook files exist, whether .env.neat is present, which OTel deps are installed.',
  { project: projectField },
  async (input) => neatDescribeProjectInstrumentation(client, { project: projectFor(input) }),
)

registerTool(
  'neat_apply_extension',
  'Install an instrumentation package and splice its registration into the existing OTel hook file. Idempotent — calling twice with the same args is a no-op. Only modifies instrumentation files, package.json, and the lockfile (via the project package manager).',
  {
    library: z.string().describe('The library being instrumented, e.g. "@prisma/client"'),
    instrumentation_package: z.string().describe('The instrumentation npm package, e.g. "@prisma/instrumentation"'),
    version: z.string().describe('Semver range for the instrumentation package, e.g. "^6.0.0"'),
    registration_snippet: z.string().describe('The JS/TS snippet to splice into the instrumentations array, e.g. "instrumentations.push(new PrismaInstrumentation())"'),
    project: projectField,
  },
  async (input) => neatApplyExtension(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'neat_dry_run_extension',
  'Preview what neat_apply_extension would do without making any changes. Returns the exact file diff, deps to add, and install command.',
  {
    library: z.string().describe('The library being instrumented, e.g. "@prisma/client"'),
    instrumentation_package: z.string().describe('The instrumentation npm package, e.g. "@prisma/instrumentation"'),
    version: z.string().describe('Semver range for the instrumentation package, e.g. "^6.0.0"'),
    registration_snippet: z.string().describe('The JS/TS snippet to splice into the instrumentations array'),
    project: projectField,
  },
  async (input) => neatDryRunExtension(client, { ...input, project: projectFor(input) }),
)

registerTool(
  'neat_rollback_extension',
  'Undo the last neat_apply_extension for a given library. Removes the dep from package.json and the registration from the hook file. Does not re-run the package manager — run install manually to sync the lockfile.',
  {
    library: z.string().describe('The library whose instrumentation should be rolled back'),
    project: projectField,
  },
  async (input) => neatRollbackExtension(client, { ...input, project: projectFor(input) }),
)

// ── Hosted connector tools (ADR-228) ──────────────────────────────────────
// The only tools that call the control plane (NEAT_CP_URL + a neat_pat_) rather
// than the daemon. connect/disconnect write connection state, never the graph.

registerTool(
  'neat_list_connectable',
  'List the providers you can connect to this hosted project (Supabase, Railway, …). Hosted only — needs NEAT_CP_URL and a NEAT_API_KEY (neat_pat_); returns a "not configured" note otherwise.',
  {},
  async () => neatListConnectable(connectorDeps),
)

registerTool(
  'neat_connect',
  'Connect a provider to this hosted project by pasting its API token — the headless path, no browser needed. NEAT verifies the token against the provider, seals it, and pulls the provider into the project graph as OBSERVED. Hosted only.',
  {
    provider: z
      .string()
      .describe('Provider id, e.g. "supabase" or "railway" (see neat_list_connectable)'),
    credential: z
      .string()
      .describe(
        "The provider's own API token — e.g. a Supabase Management token (sbp_...) or a Railway account/team token. Sealed at rest; never stored in the graph or returned.",
      ),
  },
  async (input) => neatConnect(connectorDeps, input),
)

registerTool(
  'neat_connection_status',
  "List the providers connected to this hosted project and each connection's status (connecting / healthy / error / needs reconnect). Hosted only.",
  {},
  async () => neatConnectionStatus(connectorDeps),
)

registerTool(
  'neat_disconnect',
  'Disconnect a provider from this hosted project — drops its stored connection(s). Hosted only.',
  {
    provider: z.string().describe('Provider id to disconnect, e.g. "supabase"'),
  },
  async (input) => neatDisconnect(connectorDeps, input),
)

// Resources sit alongside tools — same data, different access pattern. Read
// the per-node resource for raw attrs+edges JSON; subscribe to the incidents
// resource to be notified when new errors land. The tools above are unchanged.
const incidentsPollMs = process.env.NEAT_RESOURCE_POLL_MS
  ? Number(process.env.NEAT_RESOURCE_POLL_MS)
  : undefined
const resourceRegistration = registerResources(server, client, {
  ...(incidentsPollMs !== undefined ? { incidentsPollMs } : {}),
  ...(defaultProject ? { project: defaultProject } : {}),
})

// Before the MCP handshake, confirm the resolved endpoint is actually NEAT.
// Resolution falls back to :8080 when it can't find a project daemon, and if
// another service holds that port the server would otherwise query it and hand
// the agent an opaque HTML/404 on every tool call (#1069). A single /health
// probe separates NEAT (proceed) from a confirmed-foreign service (fail fast
// with a clear fix) from merely-unreachable (proceed — a daemon may still be
// booting, or be gated behind auth this server lacks the token for; the per-
// request path reports that cleanly). NEAT_SKIP_ENDPOINT_CHECK=1 opts out.
async function guardEndpoint(): Promise<void> {
  const skip = process.env.NEAT_SKIP_ENDPOINT_CHECK
  if (skip === '1' || skip === 'true') return

  const check = await checkEndpointIsNeat(baseUrl, { bearerToken })
  if (check.kind === 'foreign') {
    console.error(describeForeignEndpoint(baseUrl, resolved.source, check))
    process.exit(1)
  }
}

async function main(): Promise<void> {
  await guardEndpoint()
  const transport = new StdioServerTransport()
  await server.connect(transport)
}

const stopPolling = (): void => {
  resourceRegistration.stop()
}
process.on('SIGTERM', stopPolling)
process.on('SIGINT', stopPolling)

main().catch((err) => {
  console.error(err)
  process.exit(1)
})
